import { useMemo } from "react";

import { styled } from "@linaria/react";
import { match } from "ts-pattern";

import { GalaxyTheme } from "@gx/dls/theme/constants";
import { useGalaxyTheme, withTheme } from "@gx/dls/theme/GalaxyTheme";
import { PropsWithTheme } from "@gx/dls/theme/types";
import { SourceType } from "@gx/ts-contracts/services/database/models/public_pb";

import { SOURCE_TYPE_TO_ICON_MAP } from "@/pages/sources/constants";

export enum SourceIconSize {
  XSMALL = "XSMALL",
  SMALL = "SMALL",
  MEDIUM = "MEDIUM",
  LARGE = "LARGE",
}

interface SourceIconProps {
  sourceType: SourceType;
  size?: SourceIconSize;
}

const SourceIconWrapper = withTheme(styled.div<
  PropsWithTheme<{ $isLightMode: boolean; $size: SourceIconSize }>
>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ $size }) => {
    return match($size)
      .with(SourceIconSize.XSMALL, () => "2px")
      .with(SourceIconSize.SMALL, () => "2px")
      .with(SourceIconSize.MEDIUM, () => "4px")
      .with(SourceIconSize.LARGE, () => "6px")
      .exhaustive();
  }};
  border-radius: 4px;
  width: ${({ $size }) => {
    return match($size)
      .with(SourceIconSize.XSMALL, () => "16px")
      .with(SourceIconSize.SMALL, () => "20px")
      .with(SourceIconSize.MEDIUM, () => "28px")
      .with(SourceIconSize.LARGE, () => "36px")
      .exhaustive();
  }};
  height: ${({ $size }) => {
    return match($size)
      .with(SourceIconSize.XSMALL, () => "16px")
      .with(SourceIconSize.SMALL, () => "20px")
      .with(SourceIconSize.MEDIUM, () => "28px")
      .with(SourceIconSize.LARGE, () => "36px")
      .exhaustive();
  }};
  background-color: ${({ theme, $isLightMode }) =>
    $isLightMode ? theme.color.opacity.bw100 : theme.color.opacity.bw100Alt};
`);

const SourceIcon = ({ sourceType, size = SourceIconSize.MEDIUM }: SourceIconProps) => {
  const { galaxyTheme } = useGalaxyTheme();
  const isLightMode = useMemo(() => galaxyTheme === GalaxyTheme.LIGHT, [galaxyTheme]);
  return (
    <SourceIconWrapper $isLightMode={isLightMode} $size={size}>
      {SOURCE_TYPE_TO_ICON_MAP[sourceType]}
    </SourceIconWrapper>
  );
};

export default SourceIcon;
